/** 基础配置影响的跨项目生成确认；服务端返回精确任务范围，逐项目持久提交。 */
import type { LandingProductDto } from '@novel-admin/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  type ProjectProgress,
  landingProjectQueryKey,
  useLandingProjectExecution,
} from '~/data/queries';
import { landingProjectsApi } from '~/hooks/use-landing-projects';
import { Dialog } from '~/ui/Dialog';
import { ProjectProgressSummary } from './landing-project-display';
function SubmittedProgress({
  projectId,
  execution,
}: { projectId: string; execution: ProjectProgress }) {
  const query = useLandingProjectExecution(projectId, execution.id);
  return query.isError ? (
    <p role="alert">进度读取失败：{query.error.message}</p>
  ) : (
    <ProjectProgressSummary execution={query.data ?? execution} />
  );
}
export function LandingProductRegenerationDialog({
  change,
  onClose,
}: { change: NonNullable<LandingProductDto['html_change']>; onClose: () => void }) {
  const client = useQueryClient();
  const [submitted, setSubmitted] = useState<
    { id: string; execution?: ProjectProgress; error?: string }[]
  >([]);
  const start = useMutation({
    mutationFn: async () => {
      for (const project of change.affected_projects) {
        if (submitted.some((row) => row.id === project.project_id && row.execution)) continue;
        try {
          const detail = await landingProjectsApi.detail(project.project_id);
          if (!detail.can_generate)
            throw new Error(detail.generation_blocked_reason ?? '当前书籍不能投放');
          const execution = await landingProjectsApi.execute(project.project_id, {
            operation: 'generate',
            task_ids: project.task_ids,
          });
          setSubmitted((rows) => [
            ...rows.filter((row) => row.id !== project.project_id),
            { id: project.project_id, execution },
          ]);
        } catch (error) {
          setSubmitted((rows) => [
            ...rows.filter((row) => row.id !== project.project_id),
            { id: project.project_id, error: error instanceof Error ? error.message : '提交失败' },
          ]);
        }
      }
      void client.invalidateQueries({ queryKey: landingProjectQueryKey });
    },
  });
  const count = change.affected_projects.reduce(
    (total, project) => total + project.task_ids.length,
    0,
  );
  return (
    <Dialog
      title="基础配置已保存，需要重新生成并重新下载"
      subtitle="品牌、素材或链接固化在 HTML 中；之前下载的文件不会自动更新"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            {submitted.length ? '关闭，后台继续执行' : '稍后处理'}
          </button>
          <button
            type="button"
            className="btn btn-accent"
            disabled={
              !count ||
              start.isPending ||
              submitted.filter((row) => row.execution).length === change.affected_projects.length
            }
            onClick={() => start.mutate()}
          >
            {start.isPending
              ? '提交中…'
              : submitted.some((row) => row.error)
                ? '重试提交失败项目'
                : '现在重新生成'}
          </button>
        </>
      }
    >
      <div className="vstack" style={{ padding: 18, gap: 12 }}>
        <p>{change.reason}</p>
        <p>
          影响 {change.affected_projects.length} 个项目、{count}{' '}
          个任务。旧产物默认下载禁用；新文件生成后请重新下载，发布另行操作。
        </p>
        {change.affected_projects.map((project) => (
          <div key={project.project_id}>
            <a href={`/landing/projects/${encodeURIComponent(project.project_id)}`}>
              查看受影响项目
            </a>{' '}
            · {project.task_ids.length} 个任务
          </div>
        ))}
        {change.affected_legacy_task_count > 0 && (
          <p className="chip chip-warn">
            另有 {change.affected_legacy_task_count}{' '}
            个历史任务需重新生成；历史发布地址保留，迁移前请核对旧任务。
          </p>
        )}
        {submitted.map((row) => (
          <div className="card-flat" key={row.id} style={{ padding: 10 }}>
            <a href={`/landing/projects/${encodeURIComponent(row.id)}`}>查看后台进度与结果</a>
            {row.execution && <SubmittedProgress projectId={row.id} execution={row.execution} />}
            {row.error && (
              <p role="alert" style={{ color: 'var(--neg)' }}>
                {row.error}
              </p>
            )}
          </div>
        ))}
      </div>
    </Dialog>
  );
}
